import {
  ELanguage,
} from '@qmulus/frontend-shared/type/locale.ts';
import {
  AxiosResponse,
} from 'axios';
import {
  acceptHMRUpdate,
  defineStore,
} from 'pinia';

import {
  DEFAULT_USER_SETTINGS,
  useCustomUserStorageStore,
} from './custom-user-storage.ts';
import {
  asyncRequestActionList,
} from './util/request.ts';

import {
  DEFAULT_CHART_TIME_FORMAT,
  EChartTimeFormat,
} from '@/components/select-chart-time-format/select-chart-time-format.ts';
import {
  TGetAllAppsWithPermissionsResponse,
  TGetAllSettingsRouteResponse,
  TGetCurrentUserRouteResponse,
  getAllAppsWithPermissions,
  getAllSettingsRoute,
  getCurrentUserRoute,
} from '@/generated/qmulus/axios/core.ts';
import {
  components,
} from '@/generated/qmulus/openapi.ts';
import {
  EAppId,
  TAppGroup,
} from '@/types/app-type.ts';
import {
  getDefaultLanguage,
} from '@/types/localization-store.ts';
import {
  ESettingsSectionType,
} from '@/types/settings.ts';
import {
  TAppTitle,
  buildAppTitle,
} from '@/utils/app-title.ts';
import {
  EMPTY_ARRAY,
} from '@/utils/common.ts';

// Needs to move localization features here from  Vuex "localizationStore" module as customUserStorage
export const useAppStore = defineStore('app', {
  state: () => ({
    appList: undefined as TGetAllAppsWithPermissionsResponse | undefined,
    appListPromise: undefined as Promise<AxiosResponse<TGetAllAppsWithPermissionsResponse>> | undefined,
    // is prefetched in auth-middleware
    // user will be undefined in any element outside of router view, for example header & navbar
    user: undefined as TGetCurrentUserRouteResponse | undefined,
    userPromise: undefined as Promise<AxiosResponse<TGetCurrentUserRouteResponse>> | undefined,
    isNavigating: false,
    hasUnsavedData: false,
    appTitle: {} as TAppTitle,
    appGrouping: 'Department' as components['schemas']['AppGrouping'],
  }),
  getters: {
    // TODO: remove permissionName string and do proper type
    getUserHasPermission: (state) => (
      appId: components['schemas']['AppId'],
      permissionName: components['schemas']['Permission'] | string,
    ) => !!(state.user?.appPermissions?.[appId]?.[permissionName]),
    getAppGroup: (state) => (
      app: components['schemas']['App'],
    ): TAppGroup => (state.appGrouping === 'Topic' ? app.appGroupSemanticTopic : app.appGroupSemanticDepartment),
    getHasApp: (state) => (
      appId: components['schemas']['AppId'],
    ) => !!state.appList?.find((_app) => _app.appId === appId),
    getAvailableApps: (state) => {
      const customUserStorage = useCustomUserStorageStore();
      const showOnlyAvailableApps = customUserStorage.customUserSettings?.showOnlyAvailableApps ?? DEFAULT_USER_SETTINGS.showOnlyAvailableApps;

      return (!showOnlyAvailableApps ? state.appList : state.appList?.filter((app) => !!state.user?.appPermissions?.[app.appId])) ?? EMPTY_ARRAY;
    },
    isUserLoading: (state) => state.userPromise !== undefined,
    appListIsLoading: (state) => state.appListPromise !== undefined,
    locale(): ELanguage {
      const customUserStorage = useCustomUserStorageStore();
      return customUserStorage.customUserSettings?.locale || getDefaultLanguage();
    },
    /**
     * The locale may return a localeCode (en-US), but some parts of the application can only work with
     * the languageCode (en).
     */
    language(): string {
      return this.locale.split('-')[0];
    },
    chartDateFormat(): EChartTimeFormat {
      const customUserStorage = useCustomUserStorageStore();
      return customUserStorage.customUserSettings?.chartTimeFormat || DEFAULT_CHART_TIME_FORMAT;
    },
  },
  actions: {
    setAppTitle(title: TAppTitle) {
      Object.assign(this.appTitle, title);
      document.title = buildAppTitle(this.appTitle);
    },
    setHasUnsavedData(hasUnsavedData: boolean) {
      this.hasUnsavedData = hasUnsavedData;
    },
    async loadCurrentUser(force?: boolean) {
      return asyncRequestActionList({
        callback: () => getCurrentUserRoute(),
        list: this.user,
        promise: this.userPromise,
        setList: (data) => {
          this.user = data;
        },
        setPromise: (promise) => {
          this.userPromise = promise;
        },
        force,
      });
    },
    setTenantSettings(tenantSettings: TGetAllSettingsRouteResponse) {
      const appOverviewSettings = tenantSettings
        .find((appSettings) => appSettings.appId === EAppId.TENANT_SETTINGS)
        ?.sections
        .find((section) => section.sectionId === ESettingsSectionType.APP_OVERVIEW)
        ?.settings as components['schemas']['AppOverviewSettings'] | undefined;
      this.appGrouping = appOverviewSettings?.appGrouping ?? 'Department';
    },
    async loadAppList() {
      return asyncRequestActionList({
        // the tenant settings define how the apps are grouped, so both are loaded together
        callback: async () => {
          const [
            appListResponse,
            tenantSettingsResponse,
          ] = await Promise.all([
            getAllAppsWithPermissions(),
            getAllSettingsRoute(),
          ]);
          this.setTenantSettings(tenantSettingsResponse.data);
          return appListResponse;
        },
        list: this.appList,
        promise: this.appListPromise,
        setList: (data) => {
          this.appList = data;
        },
        setPromise: (promise) => {
          this.appListPromise = promise;
        },
      });
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAppStore, import.meta.hot));
}
